// --------------------------------------------------------------------------
// Components of main page including their logical connections.
// --------------------------------------------------------------------------
import React from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Box, Flex, Grid, Image, Heading, Text, Spacer, 
  Button, IconButton, Drawer, DrawerOverlay, DrawerCloseButton,
  DrawerHeader, DrawerContent, DrawerBody, DrawerFooter, useDisclosure,
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, Avatar, useTheme} from "@chakra-ui/react";
import { Menu, Logout } from "@mui/icons-material";
import { useAuth } from '../context/security';
import logo_emi from "./assets/emi_inverted.png";
import "./styles.css";


// --- BACKGROUND ---
function Background() {
  return(
    <Box className="background"></Box>
  );
};

const Copyright = () => {
  const theme = useTheme();
  
  return (
    <Box
      position="fixed"
      bottom="10px"
      right="10px"
      zIndex={0}>
      <Text
        color="black"
        fontSize={theme.fontSize.info}>
      </Text>
    </Box>
  );
};


// --- HEADER ---
function Header() {

  // 1. State, hooks, event handling and helper functions
  const theme = useTheme();
  const lastRef = React.useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { clearAuthToken } = useAuth();

  const handleLogout = () => {
    clearAuthToken(); 
    navigate('/');
  };

  // 2. JSX
  return(
    <Flex px={4} alignItems="center" className="header">
      <Flex alignItems="center" height="100%" gap={12}>
        <Image
          src={logo_emi} alt="Logo EMI"
          width="auto" height="70%"/>
        <Breadcrumb>
          <BreadcrumbItem color="white" fontFamily="label">
            <BreadcrumbLink as={Link} to="/main">Main</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
      </Flex>
      <Spacer />
      <IconButton
        variant="plain" aria-label="Menu"
        size="lg" color="white"
        icon={<Menu />}
        ref={lastRef}
        onClick={onOpen}/>
      <Drawer
        isOpen={isOpen}
        placement='right'
        onClose={onClose}
        finalFocusRef={lastRef}>
        <DrawerOverlay />
        <DrawerContent bg={theme.colors.main.drawer} color="white"> 
          <DrawerCloseButton />
          <DrawerHeader>
            <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
              <Avatar
                name={sessionStorage.getItem("username") || "Unknown"}
                bg={theme.colors.main.highlightLight}
                color={theme.colors.main.highlightDark}/>
              <Box>
                <Heading fontSize={theme.fontSize.heading}>
                  {sessionStorage.getItem("username") || "Unknown"}
                </Heading>
                <Text fontSize={theme.fontSize.info}>
                  Premium Account
                </Text>
              </Box> 
            </Flex>
          </DrawerHeader>
          <DrawerBody>
          </DrawerBody>
          <DrawerFooter>
            <Button
              leftIcon={<Logout fontSize={theme.fontSize.iconLeft} />}
              onClick={handleLogout}>
              Log out
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};


// --- BODY ---
const Body= () => {

  // 1. State, hooks, event handling and helper functions
  const theme = useTheme();
  const navigate = useNavigate();
  const handleNavigationCPT = () => {
    navigate('/cpt'); 
  };
  const handleNavigationEOx = () => {
    navigate('/eox'); 
  };
  const handleNavigationSatDB = () => {
    navigate('/satellite-db'); 
  };

  // 2. JSX
  return(
    <Box className="body-layout-column">
      <Box 
        px={4} py={4}
        width="100%" maxWidth="100%"
        bg={theme.colors.main.box}
        borderRadius={theme.radii.box}>
        <Grid
          gridTemplateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }}
          gridTemplateRows={{ base: 'repeat(3, 1fr)', md: 'none' }}
          rowGap={8} columnGap={8}>
          <Box
            px={2} py={2}
            maxWidth="400px"
            onClick={handleNavigationCPT}
            borderRadius={theme.radii.box}
            cursor="pointer"
            _hover={{
              bg: theme.colors.main.black,
            }}>
            <Flex alignItems="center">
              <Box>
                <Heading
                  mb={2}
                  fontSize={theme.fontSize.heading}>
                  Coverage Planning Tool
                </Heading>
                <Text 
                  mb={2}
                  textAlign="left"
                  fontSize={theme.fontSize.body}>
                  Identify future Earth Observation data to plan your
                  EO data ressources for your area of interest.
                </Text>
              </Box>
            </Flex>
          </Box>
          <Box
            px={2} py={2}
            maxWidth="400px"
            onClick={handleNavigationEOx}
            borderRadius={theme.radii.box}
            cursor="pointer"
            _hover={{
              bg: theme.colors.main.black,
            }}>
            <Flex alignItems="center">
              <Box>
                <Heading
                  mb={2}
                  fontSize={theme.fontSize.heading}>
                  Earth Observation Explorer
                </Heading>
                <Text 
                  mb={2}
                  textAlign="left"
                  fontSize={theme.fontSize.body}>
                  Explore existing Earth Observation data from
                  multiple archives and providers.
                </Text>
              </Box>
            </Flex>
          </Box>
          <Box
            px={2} py={2}
            maxWidth="400px"
            onClick={handleNavigationSatDB}
            borderRadius={theme.radii.box}
            cursor="pointer"
            _hover={{
              bg: theme.colors.main.black,
            }}>
            <Flex alignItems="center">
              <Box>
                <Heading
                  mb={2}
                  fontSize={theme.fontSize.heading}>
                  Satellite Database
                </Heading>
                <Text 
                  mb={2}
                  textAlign="left"
                  fontSize={theme.fontSize.body}>
                  Discover a database of operating space-borne EO
                  platforms and corresponding instruments.
                </Text>
              </Box>
            </Flex>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

// --- EXPORT PAGE ---
const MainPage = () => {
  return(
    <Box className="layout-column">
      <Header />
      <Body />
      <Background />
      <Copyright />
    </Box>
  );
};

export default MainPage;